import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { SortConfig, toggleSort, sortDataRecords, CallDataRecord, TransferDataRecord } from '../components/dashboardTypes';

const PAGE_SIZE = 100;

export type SupportedDomain = 'FOOD_ORDERING' | 'HEALTHCARE';

export interface BaseFilterState {
    searchQuery: string;
    selectedPhones: Set<string>;
    phoneFilterMode: 'include' | 'exclude';
    selectedLocations: Set<string>;
    sortConfig: SortConfig | null;
    openColumnFilter: string | null;
    currentPage: number;
}

export interface CallFilterState extends BaseFilterState {
    selectedTags: Set<string>;
    excludedTags: Set<string>;
    tagFilterMode: 'any' | 'all';
    selectedCategories: Set<string>;
}

export interface TransferFilterState extends BaseFilterState {
    selectedTransferType: 'all' | 'Planned' | 'Unplanned' | 'Undetermined';
    selectedTransferReasons: Set<string>;
}

export interface UseCallDataModalFiltersResult {
    // State
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    selectedPhones: Set<string>;
    setSelectedPhones: (phones: Set<string>) => void;
    phoneFilterMode: 'include' | 'exclude';
    setPhoneFilterMode: (mode: 'include' | 'exclude') => void;
    selectedLocations: Set<string>;
    setSelectedLocations: (locations: Set<string>) => void;
    selectedTags: Set<string>;
    setSelectedTags: (tags: Set<string>) => void;
    excludedTags: Set<string>;
    setExcludedTags: (tags: Set<string>) => void;
    tagFilterMode: 'any' | 'all';
    setTagFilterMode: (mode: 'any' | 'all') => void;
    selectedCategories: Set<string>;
    setSelectedCategories: (categories: Set<string>) => void;
    sortConfig: SortConfig | null;
    openColumnFilter: string | null;
    setOpenColumnFilter: (filter: string | null) => void;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

    // Computed
    filteredData: CallDataRecord[];
    paginatedData: CallDataRecord[];
    totalPages: number;
    hasActiveFilters: boolean;
    uniquePhones: string[];
    uniqueLocations: string[];
    uniqueCategories: string[];

    // Actions
    clearAllFilters: () => void;
    handleSort: (key: string) => void;
    handlePhoneToggle: (phone: string) => void;
}

export interface UseTransferDataModalFiltersResult {
    // State
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    selectedPhones: Set<string>;
    setSelectedPhones: (phones: Set<string>) => void;
    phoneFilterMode: 'include' | 'exclude';
    setPhoneFilterMode: (mode: 'include' | 'exclude') => void;
    selectedLocations: Set<string>;
    setSelectedLocations: (locations: Set<string>) => void;
    selectedTransferType: 'all' | 'Planned' | 'Unplanned' | 'Undetermined';
    setSelectedTransferType: (type: 'all' | 'Planned' | 'Unplanned' | 'Undetermined') => void;
    selectedTransferReasons: Set<string>;
    setSelectedTransferReasons: (reasons: Set<string>) => void;
    sortConfig: SortConfig | null;
    openColumnFilter: string | null;
    setOpenColumnFilter: (filter: string | null) => void;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

    // Computed
    filteredData: TransferDataRecord[];
    paginatedData: TransferDataRecord[];
    totalPages: number;
    hasActiveFilters: boolean;
    isOnlyExcludingDefaultReasons: boolean;
    uniquePhones: string[];
    uniqueLocations: string[];
    uniqueReasons: string[];

    // Actions
    clearAllFilters: () => void;
    handleSort: (key: string) => void;
    handlePhoneToggle: (phone: string) => void;
}

/**
 * Hook for managing Call Data Modal filtering, sorting, and pagination
 */
export function useCallDataModalFilters(
    records: CallDataRecord[],
    modalOpen: boolean,
    getTagName: (tag: string, domain: SupportedDomain) => string,
    domain: SupportedDomain = 'FOOD_ORDERING'
): UseCallDataModalFiltersResult {
    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
    const [phoneFilterMode, setPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [excludedTags, setExcludedTags] = useState<Set<string>>(new Set());
    const [tagFilterMode, setTagFilterMode] = useState<'any' | 'all'>('all');
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    // Unique values for filter dropdowns
    const uniquePhones = useMemo(() =>
        records.length > 0 ? [...new Set(records.map(c => c.phoneLastFour))].sort() : [],
        [records]
    );

    const uniqueLocations = useMemo(() =>
        records.length > 0 ? [...new Set(records.map(c => c.location))].sort() : [],
        [records]
    );

    const uniqueCategories = useMemo(() =>
        records.length > 0 ? [...new Set(records.map(c => c.category).filter(Boolean))].sort() : [],
        [records]
    );

    // Filtered data - only computed when modal is open
    const filteredData = useMemo(() => {
        if (records.length === 0 || !modalOpen) return [];

        let filtered = records.filter(call => {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = !searchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.category.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, domain).toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedPhones.size > 0) {
                matchesPhone = phoneFilterMode === 'include'
                    ? selectedPhones.has(call.phoneLastFour)
                    : !selectedPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedLocations.size === 0 || selectedLocations.has(call.location);
            const matchesTags = selectedTags.size === 0 || (
                tagFilterMode === 'any'
                    ? call.tags.some(tag => selectedTags.has(tag))
                    : [...selectedTags].every(tag => call.tags.includes(tag))
            );
            const notExcluded = excludedTags.size === 0 || !call.tags.some(tag => excludedTags.has(tag));
            const matchesCategory = selectedCategories.size === 0 || selectedCategories.has(call.category);

            return matchesSearch && matchesPhone && matchesLocation && matchesTags && notExcluded && matchesCategory;
        });

        return sortDataRecords(filtered, sortConfig);
    }, [records, searchQuery, selectedPhones, phoneFilterMode, selectedLocations, selectedTags, excludedTags, tagFilterMode, selectedCategories, sortConfig, modalOpen, getTagName, domain]);

    // Paginated data
    const paginatedData = useMemo(() => {
        const start = currentPage * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

    const hasActiveFilters = searchQuery.length > 0 ||
        selectedPhones.size > 0 ||
        selectedLocations.size > 0 ||
        selectedTags.size > 0 ||
        excludedTags.size > 0 ||
        selectedCategories.size > 0;

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(0);
    }, [searchQuery, selectedPhones, phoneFilterMode, selectedLocations, selectedTags, excludedTags, tagFilterMode, selectedCategories, sortConfig]);

    // Actions
    const clearAllFilters = useCallback(() => {
        setSearchQuery('');
        setSelectedPhones(new Set());
        setPhoneFilterMode('include');
        setSelectedLocations(new Set());
        setSelectedTags(new Set());
        setExcludedTags(new Set());
        setTagFilterMode('all');
        setSelectedCategories(new Set());
        setSortConfig(null);
        setOpenColumnFilter(null);
    }, []);

    const handleSort = useCallback((key: string) => {
        setSortConfig(prev => toggleSort(key, prev));
    }, []);

    const handlePhoneToggle = useCallback((phone: string) => {
        setSelectedPhones(prev => {
            const newSet = new Set(prev);
            if (newSet.has(phone)) {
                newSet.delete(phone);
            } else {
                newSet.add(phone);
            }
            return newSet;
        });
    }, []);

    return {
        // State
        searchQuery,
        setSearchQuery,
        selectedPhones,
        setSelectedPhones,
        phoneFilterMode,
        setPhoneFilterMode,
        selectedLocations,
        setSelectedLocations,
        selectedTags,
        setSelectedTags,
        excludedTags,
        setExcludedTags,
        tagFilterMode,
        setTagFilterMode,
        selectedCategories,
        setSelectedCategories,
        sortConfig,
        openColumnFilter,
        setOpenColumnFilter,
        currentPage,
        setCurrentPage,

        // Computed
        filteredData,
        paginatedData,
        totalPages,
        hasActiveFilters,
        uniquePhones,
        uniqueLocations,
        uniqueCategories,

        // Actions
        clearAllFilters,
        handleSort,
        handlePhoneToggle
    };
}

/**
 * Hook for managing Transfer Data Modal filtering, sorting, and pagination
 * @param defaultExcludedReasons - Reasons to exclude by default (e.g., IMMEDIATE_TRANSFER). When provided, the modal will pre-select all OTHER reasons on open.
 */
export function useTransferDataModalFilters(
    records: TransferDataRecord[],
    modalOpen: boolean,
    getReasonLabel: (reason: string) => string,
    defaultExcludedReasons?: string[]
): UseTransferDataModalFiltersResult {
    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
    const [phoneFilterMode, setPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
    const [selectedTransferType, setSelectedTransferType] = useState<'all' | 'Planned' | 'Unplanned' | 'Undetermined'>('all');
    const [selectedTransferReasons, setSelectedTransferReasons] = useState<Set<string>>(new Set());
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    // Unique values for filter dropdowns
    const uniquePhones = useMemo(() =>
        records.length > 0 ? [...new Set(records.map(t => t.phoneLastFour))].sort() : [],
        [records]
    );

    const uniqueLocations = useMemo(() =>
        records.length > 0 ? [...new Set(records.map(t => t.location))].sort() : [],
        [records]
    );

    const uniqueReasons = useMemo(() =>
        records.length > 0 ? [...new Set(records.map(t => t.transferReason))] : [],
        [records]
    );

    // Track if we've initialized the default excluded reasons filter
    const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);

    // Reset initialization flag when modal closes, so defaults re-apply on next open
    useEffect(() => {
        if (!modalOpen) {
            setHasInitializedDefaults(false);
        }
    }, [modalOpen]);

    // Initialize selected reasons to exclude defaultExcludedReasons when modal opens
    // This pre-selects all reasons EXCEPT the excluded ones
    useEffect(() => {
        if (modalOpen && !hasInitializedDefaults && uniqueReasons.length > 0 && defaultExcludedReasons && defaultExcludedReasons.length > 0) {
            const reasonsToSelect = uniqueReasons.filter(reason => !defaultExcludedReasons.includes(reason));
            if (reasonsToSelect.length > 0 && reasonsToSelect.length < uniqueReasons.length) {
                // Only pre-select if there are actually excluded reasons in the data
                setSelectedTransferReasons(new Set(reasonsToSelect));
            }
            setHasInitializedDefaults(true);
        }
    }, [modalOpen, hasInitializedDefaults, uniqueReasons, defaultExcludedReasons]);

    // Filtered data - only computed when modal is open
    const filteredData = useMemo(() => {
        if (records.length === 0 || !modalOpen) return [];

        let filtered = records.filter(transfer => {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = !searchQuery ||
                transfer.location.toLowerCase().includes(searchLower) ||
                transfer.phoneLastFour.toLowerCase().includes(searchLower) ||
                transfer.date.toLowerCase().includes(searchLower) ||
                transfer.time.toLowerCase().includes(searchLower) ||
                transfer.transferType.toLowerCase().includes(searchLower) ||
                getReasonLabel(transfer.transferReason).toLowerCase().includes(searchLower);

            let matchesPhone = true;
            if (selectedPhones.size > 0) {
                matchesPhone = phoneFilterMode === 'include'
                    ? selectedPhones.has(transfer.phoneLastFour)
                    : !selectedPhones.has(transfer.phoneLastFour);
            }

            const matchesLocation = selectedLocations.size === 0 || selectedLocations.has(transfer.location);
            const matchesType = selectedTransferType === 'all' || transfer.transferType === selectedTransferType;
            const matchesReason = selectedTransferReasons.size === 0 || selectedTransferReasons.has(transfer.transferReason);

            return matchesSearch && matchesPhone && matchesLocation && matchesType && matchesReason;
        });

        return sortDataRecords(filtered, sortConfig, true);
    }, [records, searchQuery, selectedPhones, phoneFilterMode, selectedLocations, selectedTransferType, selectedTransferReasons, sortConfig, modalOpen, getReasonLabel]);

    // Paginated data
    const paginatedData = useMemo(() => {
        const start = currentPage * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

    // Reason filter is only "active" if some (but not all) reasons are selected
    // When all reasons are selected, it's equivalent to no filter
    const hasReasonFilter = selectedTransferReasons.size > 0 && selectedTransferReasons.size < uniqueReasons.length;

    const hasActiveFilters = searchQuery.length > 0 ||
        selectedPhones.size > 0 ||
        selectedLocations.size > 0 ||
        selectedTransferType !== 'all' ||
        hasReasonFilter;

    // Check if ONLY the default excluded reasons are being filtered out
    // This is true when:
    // - No search query, no phone/location filters, transfer type is 'all'
    // - selectedTransferReasons contains exactly all reasons EXCEPT the defaultExcludedReasons
    const isOnlyExcludingDefaultReasons = useMemo(() => {
        if (!defaultExcludedReasons || defaultExcludedReasons.length === 0) return false;
        if (searchQuery.length > 0) return false;
        if (selectedPhones.size > 0) return false;
        if (selectedLocations.size > 0) return false;
        if (selectedTransferType !== 'all') return false;
        if (selectedTransferReasons.size === 0) return false;

        // Check if selected reasons are exactly all unique reasons minus the excluded ones
        const expectedReasons = uniqueReasons.filter(r => !defaultExcludedReasons.includes(r));
        if (selectedTransferReasons.size !== expectedReasons.length) return false;

        return expectedReasons.every(r => selectedTransferReasons.has(r));
    }, [searchQuery, selectedPhones, selectedLocations, selectedTransferType, selectedTransferReasons, uniqueReasons, defaultExcludedReasons]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(0);
    }, [searchQuery, selectedPhones, phoneFilterMode, selectedLocations, selectedTransferType, selectedTransferReasons, sortConfig]);

    // Actions
    const clearAllFilters = useCallback(() => {
        setSearchQuery('');
        setSelectedPhones(new Set());
        setPhoneFilterMode('include');
        setSelectedLocations(new Set());
        setSelectedTransferType('all');
        setSelectedTransferReasons(new Set());
        setSortConfig(null);
        setOpenColumnFilter(null);
        // Note: we do NOT reset hasInitializedDefaults here so "Clear all" truly shows all transfers
    }, []);

    const handleSort = useCallback((key: string) => {
        setSortConfig(prev => toggleSort(key, prev));
    }, []);

    const handlePhoneToggle = useCallback((phone: string) => {
        setSelectedPhones(prev => {
            const newSet = new Set(prev);
            if (newSet.has(phone)) {
                newSet.delete(phone);
            } else {
                newSet.add(phone);
            }
            return newSet;
        });
    }, []);

    return {
        // State
        searchQuery,
        setSearchQuery,
        selectedPhones,
        setSelectedPhones,
        phoneFilterMode,
        setPhoneFilterMode,
        selectedLocations,
        setSelectedLocations,
        selectedTransferType,
        setSelectedTransferType,
        selectedTransferReasons,
        setSelectedTransferReasons,
        sortConfig,
        openColumnFilter,
        setOpenColumnFilter,
        currentPage,
        setCurrentPage,

        // Computed
        filteredData,
        paginatedData,
        totalPages,
        hasActiveFilters,
        isOnlyExcludingDefaultReasons,
        uniquePhones,
        uniqueLocations,
        uniqueReasons,

        // Actions
        clearAllFilters,
        handleSort,
        handlePhoneToggle
    };
}
